
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { SessionHeader } from "./components/SessionHeader";
import { SessionItem } from "./components/SessionItem";
import { useTrainingSessions } from "./hooks/useTrainingSessions";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

export const StudentTrainingSessionsList = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const { sessions, isLoading, studentProfile } = useTrainingSessions();
  const queryClient = useQueryClient();
  const { t } = useTranslations("student-sessions");
  const { getPath } = useLanguagePath();

  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleDeleteSession = async (sessionId: string) => {
    setSessionToDelete(sessionId);
  };

  const confirmDelete = async () => {
    if (!sessionToDelete) return;
    
    try {
      setDeletingSession(true);
      
      // Verificar se a sessão existe e é do aluno
      const { data: session, error: sessionError } = await supabase
        .from('training_sessions')
        .select('created_by_type')
        .eq('id', sessionToDelete)
        .single();
        
      if (sessionError) {
        throw sessionError;
      }
      
      // Verificar se o aluno é o criador da sessão
      if (session.created_by_type !== 'student') {
        toast.error(t("notifications.error"));
        return;
      }

      const { error } = await supabase
        .from('training_sessions')
        .delete()
        .eq('id', sessionToDelete);

      if (error) {
        throw error;
      }

      toast.success(t("notifications.deleted"));
      queryClient.invalidateQueries({ queryKey: ['student-training-sessions'] });
      
    } catch (error) {
      console.error('Erro ao apagar a aula:', error);
      toast.error(t("notifications.error"));
    } finally {
      setSessionToDelete(null);
      setDeletingSession(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text dark:text-white">{t("title")}</h2>
          <p className="text-muted-foreground dark:text-gray-300">
            {t("subtitle")}
          </p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/student-dashboard")} className="text-flame hover:text-flame/80">{t("breadcrumbs.dashboard")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">{t("title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <SessionHeader />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground dark:text-gray-400" />
        </div>
      ) : !sessions?.length ? (
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border border-slate-100/50 dark:border-gray-700/50">
          <CardHeader>
            <CardTitle className="dark:text-white">{t("noSessions.title")}</CardTitle>
            <CardDescription className="dark:text-gray-300">
              {t("noSessions.description")}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              isOpen={openItems[session.id]}
              onToggle={() => toggleItem(session.id)}
              onDelete={session.created_by_type === 'student' ? handleDeleteSession : undefined}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!sessionToDelete} onOpenChange={(open) => !open && setSessionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">{t("deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              {t("deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">{t("deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={deletingSession}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingSession ? t("deleteDialog.deleting") : t("deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
